import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionPhase, AgentStreamEventV1 } from '@/model/Agent.model';
import type { FC } from 'react';
import styles from './InVivoSendValidator.module.scss';

interface SendProgressProps {
  phase: AgentSessionPhase;
  streamEvents: Array<AgentStreamEventV1>;
  error: string | null;
  /** Copy shown for each phase when no stream events have arrived yet. */
  placeholder: Record<AgentSessionPhase, string>;
  testId?: string;
}

const humanizePhase = (phase: string): string =>
  phase.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

interface FeedItem {
  key: string;
  label: string;
  done: boolean;
}

/**
 * Fold the raw SSE event log into an ordered activity feed: execution starts,
 * phase transitions (started → completed flips a row to done), and tool calls.
 * Mirrors the Study Protocol Parser's ProtocolProgress feed builder.
 */
const buildFeedItems = (events: Array<AgentStreamEventV1>): Array<FeedItem> => {
  const phaseStartIndex = new Map<string, number>();
  const items: Array<FeedItem> = [];

  events.forEach((event, index) => {
    if (event.type === 'execution_started') {
      items.push({ key: `start-${index}`, label: 'Execution started', done: true });
      return;
    }
    if (event.type === 'phase_transition') {
      const phase = event.phase;
      const status = event.status;
      if (_isNil(phase)) {
        return;
      }
      if (status === 'started') {
        phaseStartIndex.set(phase, items.length);
        items.push({ key: `phase-${phase}-${index}`, label: humanizePhase(phase), done: false });
        return;
      }
      if (status === 'completed') {
        const startIdx = phaseStartIndex.get(phase);
        if (_notNil(startIdx)) {
          items[startIdx] = { ...items[startIdx], done: true };
        } else {
          items.push({ key: `phase-${phase}-${index}`, label: humanizePhase(phase), done: true });
        }
      }
      return;
    }
    if (event.type === 'tool_call_start') {
      const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
      items.push({
        key: `tool-start-${index}`,
        label: `${step}${event.tool_name ?? 'tool'} (running)`,
        done: false,
      });
      return;
    }
    if (event.type === 'tool_call_end') {
      const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
      items.push({ key: `tool-end-${index}`, label: `${step}${event.tool_name ?? 'tool'}`, done: true });
    }
  });

  return items;
};

/**
 * Right-hand "Agent activity" panel for the SEND Validator screen. Renders the
 * live streamed feed for the detection and validation executions: each coarse
 * phase transition and tool call becomes a step row that flips to done as the
 * matching completion event arrives.
 */
const SendProgress: FC<SendProgressProps> = ({
  phase,
  streamEvents,
  error,
  placeholder,
  testId = 'invivo-send-validator__progress',
}) => {
  const items = buildFeedItems(streamEvents);
  return (
    <div
      className="ui-card pa4 flex flex-column"
      data-test-component="SendProgress"
      data-test-element="container"
      data-testid={testId}
    >
      <h2 className="f4 fw5 ma0 mb3" data-testid="invivo-send-validator__progress-title">
        Agent activity
      </h2>
      {phase === 'failed' && (
        <div className="red mb3" data-testid="invivo-send-validator__error">
          {error ?? 'SEND validation failed.'}
        </div>
      )}
      {items.length === 0 ? (
        <div className="dark-gray" data-testid="invivo-send-validator__placeholder">
          {placeholder[phase]}
        </div>
      ) : (
        <ol className={styles.feed} data-testid="invivo-send-validator__feed">
          {items.map((item) => (
            <li key={item.key} className={`${styles.feedItem} ${item.done ? styles.feedItemDone : ''}`}>
              {item.done ? '✓ ' : '… '}
              {item.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default SendProgress;
